import debounceWithResolver from '../debounceWithResolver';
import createAsyncMutex from '../createAsyncMutex';
import fetchJSON from '../fetchJSON';
import {omit, isEqual} from 'lodash';
import fetchElevation from './fetchElevation';
import {AddSavePromise} from '../../hooks/useIsSaved';
import createExposedPromise, {ExposedPromise} from '../createExposedPromise';

const debouncedFetch = debounceWithResolver(window.fetch, 500);

const savedPoints: Record<string, Waypoint | TrackPoint> = {};
const remoteIds: Record<string, RemoteId> = {};

interface RemoteId extends ExposedPromise<string> {
    sync?: string;
}

function createRemoteId(initialId: string | undefined): RemoteId {
    const exposedPromise = createExposedPromise<string>();
    const remoteId: RemoteId = {
        promise: exposedPromise.promise,
        resolve: (id: string) => {
            exposedPromise.resolve(id);
            remoteId.sync = id;
        },
        reject: (error: any) => {
            exposedPromise.reject(error);
        },
    };
    if (initialId !== undefined) remoteId.resolve(initialId);
    return remoteId;
}

function getIsModifiedSinceSave(point: Waypoint | TrackPoint) {
    const {pointLocalId} = point.properties;
    const savedPoint = savedPoints[pointLocalId];

    // Do not save if they havent changed since the last time they were saved
    if (savedPoint && isEqual(savedPoint, point)) return false;

    // Do not save if they have alreday been deleted remotely
    if (savedPoint && savedPoint.properties.isLocallyDeleted) return false;

    return true;
}

export function registerSavedPoint(point: Waypoint | TrackPoint) {
    savedPoints[point.properties.pointLocalId] = point;
}

export function getPointRemoteId(point: Waypoint | TrackPoint) {
    if (!remoteIds[point.properties.pointLocalId])  {
        remoteIds[point.properties.pointLocalId] = createRemoteId(point.properties.id);
    }
    return remoteIds[point.properties.pointLocalId].promise;
}

export default createAsyncMutex(async (
    point: TrackPoint | Waypoint,
    addSavePromise: AddSavePromise,
): Promise<void> => {

    const {pointLocalId} = point.properties;
    // Populate points's remoteId if it doesn't yet exist
    getPointRemoteId(point);
    const remoteId = remoteIds[point.properties.pointLocalId];

    const isModifiedSinceSave = getIsModifiedSinceSave(point);
    savedPoints[pointLocalId] = point;
    if (!isModifiedSinceSave) return;

    const body = JSON.stringify(omit(
        {...point, geometry: await fetchElevation(point.geometry)},
        'properties.latitude',
        'properties.longitude'
    ));

    if (point.properties.isLocallyDeleted) {
        if (remoteId.sync) {
            addSavePromise(window.fetch(`/api/objects/waypoint/${remoteId.sync}/`, {
                credentials: 'same-origin',
                method: 'DELETE',
            }));
        }
        delete remoteIds[point.properties.pointLocalId];

    } else if (remoteId.sync) {
        await addSavePromise(debouncedFetch(`/api/objects/waypoint/${remoteId.sync}/`, {
            method: 'PUT',
            credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'},
            body,
        }));

    } else {
        const response = await addSavePromise(fetchJSON('/api/objects/waypoint/', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'},
            body,
        }));

        remoteId.resolve(response.properties.id);
    }
}, point => point.properties.pointLocalId);
